import React, { useState } from "react";
import { Formik } from "formik";
import { Form, InputGroup, Button, Container, Row, Col } from "react-bootstrap";
import { AiOutlineExclamationCircle, AiOutlinePlus } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import {
  getResidenceInputs,
  getValidationSchema,
} from "../formEntities/residenceEntities";

const ResidenceForm = ({
  shake,
  handleExtendedAddress,
  extendedAddress,
  residences,
  handleFormikSubmit,
  initialValues,
  userData,
  updatePage,
  canUseSevenYearCheckbox
}) => {
  const { t } = useTranslation();
  const residenceInputs = getResidenceInputs(t);
  const validationSchema = getValidationSchema(t);
  const [address2, setAddress2] = useState(false);

  // Convert MM/DD/YYYY to YYYY-MM-DD for date input
  const toDateInputFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return "";
    const [month, day, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for storage
  const toStorageFormat = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  };

  // Get today's date in YYYY-MM-DD format for max attribute
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const styles = {
    errorStyle: {
      border: "2px solid red",
      borderTopRightRadius: "0",
      borderBottomRightRadius: "0",
    },
    fieldErrMessage: {
      color: "red",
      paddingLeft: "5px",
    },
    errIcon: {
      fontSize: "20px",
      color: "red",
    },
    inputGroupTextStyle: {
      border: "1px solid red",
      background: "white",
    },
    errText: {
      color: "red",
    },
  };

  const handleValidations = (values) => {
    if (extendedAddress) {
      handleFormikSubmit(values);
    } else {
      let errors = {};

      // Validate fromDate is not in the future
      if (values.fromDate && values.fromDate !== "") {
        const fromDate = new Date(values.fromDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (fromDate > today) {
          errors.fromDate = t("residence.validation.fromDateFuture");
        }
      }

      // Validate untillDate is not in the future
      if (values.untillDate && values.untillDate !== "") {
        const untillDate = new Date(values.untillDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (untillDate > today) {
          errors.untillDate = t("residence.validation.untilDateFuture");
        }
      }

      // Validate untillDate is after fromDate
      if (values.fromDate && values.untillDate) {
        const fromDate = new Date(values.fromDate);
        const untillDate = new Date(values.untillDate);
        if (untillDate < fromDate) {
          errors.untillDate = t("residence.validation.untilAfterFrom");
        }
      }

      if (values.zip && !/^[0-9]+$/i.test(values.zip)) {
        errors.zip = t("residence.validation.zipNumeric");
      }
      return errors;
    }
  };

  const handleAdd = (values) => {
    if (
      !values.address1 ||
      !values.city ||
      !values.state ||
      !values.zip ||
      !values.fromDate ||
      !values.untillDate
    ) {
      shake();
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      validate={(values) => handleValidations(values)}
      onSubmit={(values, actions) => handleFormikSubmit(values, actions)}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
        <Container>
          {/* {console.log("Values:", values, "\nErrors:", errors)} */}
          <Form noValidate onSubmit={handleSubmit}>
            {/* Skip Step Checkbox */}
            {residences.length === 0 && (
              <section className="text-start">
                <p>{t("residence.form.skipStepPrompt")}</p>
                {!canUseSevenYearCheckbox() && residences.length === 0 && (
                  <div className="alert alert-warning" role="alert">
                    {t("residence.form.cannotUseCheckbox")}
                  </div>
                )}
                <Form.Group
                  className="skip-this-step text-start"
                  controlId="form-basic-checkbox"
                >
                  <Form.Check
                    type="checkbox"
                    label={t("residence.form.sevenYearCheckbox")}
                    onChange={() => handleExtendedAddress()}
                    checked={extendedAddress ? true : false}
                    value={extendedAddress}
                    disabled={!canUseSevenYearCheckbox()}
                  />
                </Form.Group>
              </section>
            )}
            <hr />

            {/* Inputs  */}
            <section>
              <Container>
                <Row>
                  {residenceInputs.map((input, index) => (
                    <Col sm={12} md={6} key={index}>
                      {input.name === "address2" && !address2 && (
                        <div
                          className="text-start d-flex align-items-center"
                          style={{ height: "100%" }}
                        >
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              textDecoration: "underline",
                            }}
                            onClick={() => setAddress2(true)}
                          >
                            <AiOutlinePlus className="me-2" />{" "}
                            {t("common.addressLine2")}
                          </button>
                        </div>
                      )}
                      <Form.Group
                        style={
                          input.name === "address2" && !address2
                            ? { visibility: "hidden", display: "none" }
                            : {}
                        }
                        className="mb-3 text-start"
                        controlId={index}
                      >
                        <Form.Label>{input.label}</Form.Label>
                        <InputGroup>
                          {input.prependIcon && (
                            <InputGroup.Text
                              id="inputGroupPrepend"
                              style={
                                errors[input.name] &&
                                touched[input.name] &&
                                styles.inputGroupTextStyle
                              }
                            >
                              @
                            </InputGroup.Text>
                          )}
                          <Form.Control
                            type={input.name === "fromDate" || input.name === "untillDate" ? "date" : input.type}
                            placeholder={input.name === "fromDate" || input.name === "untillDate" ? undefined : input.placeholder}
                            name={input.name}
                            onChange={input.name === "fromDate" || input.name === "untillDate"
                              ? (e) => setFieldValue(input.name, toStorageFormat(e.target.value))
                              : handleChange}
                            max={input.name === "fromDate" || input.name === "untillDate" ? getTodayString() : undefined}
                            maxLength={input.name === "zip" ? 5 : null}
                            value={input.name === "fromDate" || input.name === "untillDate"
                              ? toDateInputFormat(values[input.name])
                              : values[input.name]}
                            style={
                              errors[input.name] && touched[input.name]
                                ? styles.errorStyle
                                : null
                            }
                            disabled={userData.submissionDate}
                          />
                          {errors[input.name] && touched[input.name] && (
                            <InputGroup.Text style={styles.inputGroupTextStyle}>
                              <AiOutlineExclamationCircle
                                style={styles.errIcon}
                              />
                            </InputGroup.Text>
                          )}
                        </InputGroup>
                        {errors[input.name] && touched[input.name] ? (
                          <div style={styles.fieldErrMessage}>
                            {errors[input.name]}
                          </div>
                        ) : null}
                      </Form.Group>
                    </Col>
                  ))}
                  <span>
                    <Form.Check
                      id="currentAddress"
                      label={t("residence.form.currentAddress")}
                      type="checkbox"
                      name="currentResidence"
                      checked={values.currentResidence}
                      onChange={handleChange}
                      onClick={(e) => {
                        const isChecked = e.target.checked;
                        setFieldValue("currentResidence", isChecked);
                        if (isChecked) {
                          let dateToday = new Date();
                          let month = String(dateToday.getMonth() + 1).padStart(2, '0');
                          let day = String(dateToday.getDate()).padStart(2, '0');
                          let dateNow = month + "/" + day + "/" + dateToday.getFullYear();
                          setFieldValue("untillDate", dateNow);
                        } else {
                          setFieldValue("untillDate", "");
                        }
                      }}
                    />
                  </span>
                </Row>
              </Container>
            </section>

            <Button className="m-3 btn btn-primary" type="submit" onClick={() => handleAdd(values)}>
              {t("common.add")}
            </Button>
          </Form>
        </Container>
      )}
    </Formik>
  );
};

export default ResidenceForm;
